import React from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Button,
    Divider,
    Stack,
    TextField,
    Typography,
    Tabs,
    Tab,
    Paper,
    Alert,
    IconButton,
    Tooltip,
} from '@mui/material';
import { useQuery, gql, useMutation } from '@apollo/client';
import { Controller, useForm } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import { Visibility, Code, Send } from '@mui/icons-material';

export function EmailSetup({ title, onSubmit, defaultValues, eventId, role }) {
    const [feature, setFeature] = React.useState(null);
    const [previewTab, setPreviewTab] = React.useState(0);
    const [watchValues, setWatchValues] = React.useState({});
    const { control, formState, handleSubmit, reset, watch } = useForm({
        defaultValues: {
            senderName: '',
            senderEmail: '',
            senderEmailPassword: '',
            senderHost: '',
            senderSubject: '',
            senderBody: '',
            enquiryBody: '',
            to: '',
        },
    });

    // Watch form values for preview
    const watchedValues = watch();
    const { data: featureData, loading: featureLoading } = useQuery(GET_VERIFICATION_FEATURE, {
        variables: { eventId, role },
    });

    React.useEffect(() => {
        if (defaultValues) {
            reset(defaultValues);
        }
    }, [defaultValues, reset]);

    React.useEffect(() => {
        if (featureData && featureData.mailVerificationFeature) {
            let parsedData = [];
            try {
                parsedData = JSON.parse(featureData.mailVerificationFeature);
            } catch (err) {
                console.error('Error parsing the data:', err);
            }
            if (Array.isArray(parsedData)) {
                setFeature(parsedData);
            }
        }
    }, [featureData]);

    // Function to check if content contains HTML tags
    const containsHTML = content => {
        if (!content) return false;
        const htmlRegex = /<[^>]*>/;
        return htmlRegex.test(content);
    };

    // Function to render HTML preview
    const renderHTMLPreview = content => {
        if (!content) return 'No content to preview';

        if (containsHTML(content)) {
            return (
                <Box
                    sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        p: 2,
                        bgcolor: 'background.paper',
                        minHeight: 200,
                        '& *': {
                            fontFamily: 'inherit',
                        },
                    }}
                    dangerouslySetInnerHTML={{ __html: content }}
                />
            );
        } else {
            return (
                <Box
                    sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        p: 2,
                        bgcolor: 'background.paper',
                        minHeight: 200,
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                    }}
                >
                    {content}
                </Box>
            );
        }
    };
    if (featureLoading) {
        return (
            <>
                <span>Loading...</span>
            </>
        );
    }
    return (
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2} my={1}>
                <Typography fontWeight={'bold'}>{title}</Typography>

                {/* Preview Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={previewTab} onChange={(e, newValue) => setPreviewTab(newValue)}>
                        <Tab label='Compose' icon={<Code />} iconPosition='start' />
                        <Tab
                            label='Preview'
                            icon={<Visibility />}
                            iconPosition='start'
                            disabled={!watchedValues.senderBody}
                        />
                    </Tabs>
                </Box>

                {previewTab === 0 && (
                    <Stack spacing={1}>
                        <Controller
                            control={control}
                            name='senderName'
                            rules={{ required: 'Required' }}
                            render={({ field: { ref, ...field }, fieldState: { error } }) => (
                                <TextField
                                    inputRef={ref}
                                    {...field}
                                    required
                                    error={!!error}
                                    helperText={error?.message}
                                    fullWidth
                                    label='Sender Name'
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            rules={{ required: 'Required' }}
                            name='senderEmail'
                            render={({ field: { ref, ...field }, fieldState: { error } }) => (
                                <TextField
                                    sx={{ display: 'none' }}
                                    inputRef={ref}
                                    {...field}
                                    required
                                    error={!!error}
                                    helperText={error?.message}
                                    fullWidth
                                    label='Sender Email'
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name='senderEmailPassword'
                            rules={{ required: 'Required' }}
                            render={({ field: { ref, ...field }, fieldState: { error } }) => (
                                <TextField
                                    inputRef={ref}
                                    sx={{ display: 'none' }}
                                    {...field}
                                    required
                                    error={!!error}
                                    helperText={error?.message}
                                    fullWidth
                                    label='Sender Email Password'
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name='senderHost'
                            rules={{ required: 'Required' }}
                            render={({ field: { ref, ...field }, fieldState: { error } }) => (
                                <TextField
                                    inputRef={ref}
                                    sx={{ display: 'none' }}
                                    {...field}
                                    required
                                    error={!!error}
                                    helperText={error?.message}
                                    fullWidth
                                    label='Sender Host'
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name='senderSubject'
                            rules={{ required: 'Required' }}
                            render={({ field: { ref, ...field }, fieldState: { error } }) => (
                                <TextField
                                    inputRef={ref}
                                    {...field}
                                    required
                                    error={!!error}
                                    helperText={error?.message}
                                    fullWidth
                                    label='Subject'
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name='senderBody'
                            rules={{ required: 'Required' }}
                            render={({ field: { ref, ...field }, fieldState: { error } }) => (
                                <TextField
                                    inputRef={ref}
                                    {...field}
                                    required
                                    error={!!error}
                                    helperText={error?.message}
                                    fullWidth
                                    label='Email Body'
                                    multiline
                                    minRows={6}
                                    placeholder='Enter your email content here. You can use HTML tags for formatting...'
                                />
                            )}
                        />

                        {/* HTML Detection Alert */}
                        {watchedValues.senderBody && containsHTML(watchedValues.senderBody) && (
                            <Alert severity='info' sx={{ mt: 1 }}>
                                <Typography variant='body2'>
                                    <strong>HTML Content Detected!</strong> Your email contains HTML tags. Use the
                                    Preview tab to see how it will look to recipients.
                                </Typography>
                            </Alert>
                        )}
                    </Stack>
                )}

                {previewTab === 1 && (
                    <Box>
                        <Typography variant='h6' gutterBottom>
                            Email Preview
                        </Typography>
                        <Typography variant='body2' color='text.secondary' gutterBottom>
                            Subject: {watchedValues.senderSubject || 'No subject'}
                        </Typography>
                        {renderHTMLPreview(watchedValues.senderBody)}

                        {watchedValues.senderBody && containsHTML(watchedValues.senderBody) && (
                            <Alert severity='success' sx={{ mt: 2 }}>
                                <Typography variant='body2'>
                                    <strong>HTML Preview Active!</strong> This is how your email will appear to
                                    recipients. The original HTML code will be sent to the backend.
                                </Typography>
                            </Alert>
                        )}
                    </Box>
                )}

                <Divider />

                <Stack direction='row' spacing={1} justifyContent='space-between'>
                    <Stack direction='row' spacing={1}>
                        <LoadingButton
                            loading={formState.isSubmitting}
                            type='submit'
                            color='primary'
                            variant='contained'
                            startIcon={<Send />}
                        >
                            Send Email
                        </LoadingButton>
                        <Button color='error' onClick={() => reset()}>
                            Reset
                        </Button>
                    </Stack>

                    {watchedValues.senderBody && containsHTML(watchedValues.senderBody) && (
                        <Tooltip title='Switch to Preview to see HTML rendering'>
                            <Button variant='outlined' onClick={() => setPreviewTab(1)} startIcon={<Visibility />}>
                                Preview HTML
                            </Button>
                        </Tooltip>
                    )}
                </Stack>
            </Stack>
        </form>
    );
}

export const GET_VERIFICATION_FEATURE = gql`
    query ($eventId: String!, $role: ROLE!) {
        mailVerificationFeature(eventId: $eventId, role: $role)
    }
`;
